import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Desktop from './components/Desktop/Desktop';
import Taskbar from './components/Desktop/Taskbar';
import customBackground from './backgrounds/custom.jpg';
import customBackground2 from './backgrounds/custom2.png';

function App() {
  // Detect if device is mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Load theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('webos-theme');
    return savedTheme || 'dark';
  });
  
  // Load wallpaper from localStorage or default to space wallpaper
  const [currentWallpaper, setCurrentWallpaper] = useState(() => {
    const savedWallpaper = localStorage.getItem('webos-wallpaper');
    return savedWallpaper || '/assets/wallpapers/thumb-1920-1276648.png';
  });
  
  // Load open apps from localStorage or default to empty array
  const [openApps, setOpenApps] = useState(() => {
    try {
      const savedOpenApps = localStorage.getItem('webos-open-apps');
      return savedOpenApps ? JSON.parse(savedOpenApps) : [];
    } catch (e) {
      console.error('Failed to load open apps from localStorage:', e);
      return [];
    }
  });

  // Check for version to update wallpaper if first time with new version
  useEffect(() => {
    const currentVersion = '1.1.0'; // Increment this when making UI changes
    const savedVersion = localStorage.getItem('webos-version');
    
    // If new version or first visit, update default wallpaper
    if (!savedVersion || savedVersion !== currentVersion) {
      // Set the space wallpaper as default for all users
      setCurrentWallpaper('/assets/wallpapers/thumb-1920-1276648.png');
      // Save version to localStorage
      localStorage.setItem('webos-version', currentVersion);
    }
  }, []);

  // Detect window resize and update isMobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset webOS state (uncomment to reset everything)
  /* 
  useEffect(() => {
    // Clear all localStorage data to reset the system
    localStorage.removeItem('webos-open-apps');
    localStorage.removeItem('webos-desktop-icons');
    localStorage.removeItem('webos-installed-apps');
    // Set the default wallpaper to the space theme - but don't clear it
    localStorage.setItem('webos-wallpaper', '/assets/wallpapers/thumb-1920-1276648.png');
    console.log("WebOS state reset completed");
    
    // Force a page refresh after clearing localStorage
    window.location.reload();
  }, []);
  */

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('webos-theme', theme);
  }, [theme]);

  // Save wallpaper to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('webos-wallpaper', currentWallpaper);
  }, [currentWallpaper]);

  // Save open apps to localStorage when they change
  useEffect(() => {
    // Clean the openApps array to remove any DOM references that would cause circular JSON error
    const cleanOpenApps = openApps.map(app => {
      // Create a clean copy without DOM references
      const cleanApp = { ...app };
      
      // Remove any properties that might contain DOM references
      if (cleanApp.windowElement) delete cleanApp.windowElement;
      if (cleanApp.ref) delete cleanApp.ref;
      if (cleanApp.element) delete cleanApp.element;
      
      // Also clean any nested objects that might contain references
      if (cleanApp.mouseRef) delete cleanApp.mouseRef;
      
      // Remove window object references that cause circular JSON errors
      if (cleanApp.window) delete cleanApp.window;
      
      return cleanApp;
    });
    
    try {
      // Save the cleaned array to localStorage
      localStorage.setItem('webos-open-apps', JSON.stringify(cleanOpenApps));
    } catch (error) {
      console.error('Error saving open apps to localStorage:', error);
      
      // If it fails, try a more aggressive cleaning approach
      const safeApps = cleanOpenApps.map(app => ({
        id: app.id,
        name: app.name,
        title: app.title,
        icon: app.icon,
        component: app.component,
        className: app.className,
        url: app.url,
        content: app.content
      }));
      
      localStorage.setItem('webos-open-apps', JSON.stringify(safeApps));
    }
    
    // Automatically adjust taskbar layout based on number of open apps
    adjustTaskbarForOpenApps();
  }, [openApps]);

  // Adjust taskbar layout based on number of open apps
  const adjustTaskbarForOpenApps = () => {
    const taskbar = document.querySelector('.taskbar');
    if (!taskbar) return;
    
    // Get screen width for better responsive layout
    const screenWidth = window.innerWidth;
    
    // Adjust max-width of taskbar based on number of open apps and screen size
    if (screenWidth <= 480) {
      // Small mobile devices - always large percentage
      taskbar.style.maxWidth = '95%';
    } else if (screenWidth <= 768) {
      // Mobile devices - larger percentage
      if (openApps.length > 2) {
        taskbar.style.maxWidth = '95%';
      } else {
        taskbar.style.maxWidth = '85%';
      }
    } else {
      // Tablets, laptops and desktops
      if (openApps.length > 10) {
        // More than 10 apps - maximum expansion
        taskbar.style.maxWidth = '95%';
      } else if (openApps.length > 8) {
        // 9-10 apps - very wide
        taskbar.style.maxWidth = '90%';
      } else if (openApps.length > 6) {
        // 7-8 apps - wide
        taskbar.style.maxWidth = '85%';
      } else if (openApps.length > 4) {
        // 5-6 apps - medium-wide
        taskbar.style.maxWidth = '75%';
      } else if (openApps.length > 2) {
        // 3-4 apps - slightly wider than default
        taskbar.style.maxWidth = '68%';
      } else {
        // 0-2 apps - default compact width
        taskbar.style.maxWidth = '60%';
      }
    }
  };

  // Inside the App component, add a CSS style block for global animations
  useEffect(() => {
    // Create a style element for animations
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }

      @keyframes fadeOut {
        from { opacity: 1; transform: scale(1); }
        to { opacity: 0; transform: scale(0.95); }
      }

      @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      @keyframes slideDown {
        from { transform: translateY(-20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      @keyframes bounceIn {
        0% { transform: scale(0.3); opacity: 0; }
        40% { transform: scale(1.1); opacity: 1; }
        60% { transform: scale(0.9); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }

      @keyframes zoomInOut {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      .webos-app {
        animation: fadeIn 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2);
      }

      .webos-closing {
        animation: fadeOut 0.2s ease-out forwards;
      }

      .taskbar {
        transition: all 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2) !important;
      }

      .desktop-icon:hover {
        animation: zoomInOut 0.5s ease infinite;
      }

      .light-theme {
        transition: background-color 0.8s ease;
      }

      .dark-theme {
        transition: background-color 0.8s ease;
      }
    `;
    
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Modify the launchApp function to add smooth animations
  const launchApp = useCallback((app) => {
    // Check if this is a duplicate app that should not be launched again
    const isDuplicate = openApps.some(existingApp => {
      // For web pages, only check for duplicate component types
      if (app.component === 'WebBrowser') {
        return existingApp.component === app.component && existingApp.url === app.url;
      }
      return existingApp.component === app.component;
    });

    if (isDuplicate) {
      console.log("App is already open, focusing instead of launching new instance");
      // Find the existing instance
      const existingApp = openApps.find(existingApp => {
        if (app.component === 'WebBrowser') {
          return existingApp.component === app.component && existingApp.url === app.url;
        }
        return existingApp.component === app.component;
      });

      if (existingApp) {
        // Toggle its visibility via custom event
        const toggleEvent = new CustomEvent('webos-toggle-window', { 
          detail: { appId: existingApp.id }
        });
        window.dispatchEvent(toggleEvent);
      }
      return;
    }
    
    // Add animation class to app object and ensure icon classes are properly formed
    const appWithAnimation = {
      ...app,
      animateIn: true, // Flag for animation
      className: app.className || 'webos-app' // Default class for animation
    };

    // Fix icon classes for FontAwesome
    if (app.icon && !app.className) {
      // Set proper icon class based on icon type
      const brandIcons = ['chrome', 'edge', 'spotify', 'facebook', 'twitter', 'windows', 'youtube'];
      if (brandIcons.includes(app.icon.toLowerCase())) {
        appWithAnimation.className = `fab fa-${app.icon.toLowerCase()}`;
      } else {
        appWithAnimation.className = `fas fa-${app.icon.toLowerCase()}`;
      }
    }

    setOpenApps(prevApps => [...prevApps, appWithAnimation]);
  }, [openApps]);

  // Modify closeApp function to add smooth exit animation
  const closeApp = useCallback((appId) => {
    // Find the app element by its ID
    const appElement = document.querySelector(`[data-app-id="${appId}"]`);
    
    if (appElement) {
      // Add closing animation class
      appElement.classList.add('webos-closing');
      
      // Wait for animation to complete before removing from state
      setTimeout(() => {
        setOpenApps(prevApps => prevApps.filter(app => app.id !== appId));
      }, 200); // Match this with the fadeOut animation duration
    } else {
      // If element not found, just remove it without animation
      setOpenApps(prevApps => prevApps.filter(app => app.id !== appId));
    }
  }, []);

  // Enhance theme toggle with animation
  const toggleTheme = () => {
    // Add a temporary overlay for transition effect
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    overlay.style.zIndex = '9999';
    overlay.style.pointerEvents = 'none';
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.5s ease';
    document.body.appendChild(overlay);
    
    // Trigger animation
    setTimeout(() => {
      overlay.style.opacity = '0.3';
    }, 10);
    
    // Change theme after brief delay
    setTimeout(() => {
      setTheme(prev => prev === 'dark' ? 'light' : 'dark');
      
      // Fade out overlay
      overlay.style.opacity = '0';
      
      // Remove overlay after animation completes
      setTimeout(() => {
        document.body.removeChild(overlay);
      }, 500);
    }, 300);
  };

  // Add app to desktop from Store
  const addAppToDesktop = (app) => {
    console.log('Adding app to desktop:', app);
    
    // Call the global addDesktopIcon function exposed by Desktop.js
    if (window.addDesktopIcon) {
      window.addDesktopIcon(app);
    } else {
      console.error('addDesktopIcon function not available');
    }
    
    // Save to installed apps in localStorage
    try {
      const installedApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      installedApps[app.id] = true;
      localStorage.setItem('webos-installed-apps', JSON.stringify(installedApps));
    } catch (e) {
      console.error('Failed to save installed app:', e);
    }
  };

  // Change wallpaper function
  const changeWallpaper = (wallpaperPath) => {
    setCurrentWallpaper(wallpaperPath);
  };

  // Load settings and initialize default values on app startup
  useEffect(() => {
    // Clean up music player app if it exists (removing it from the store)
    try {
      const installedApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      if (installedApps['music-player']) {
        delete installedApps['music-player'];
        localStorage.setItem('webos-installed-apps', JSON.stringify(installedApps));
        console.log('Music Player app removed from installed apps.');
      }
      
      // Also remove from desktop icons if present
      const desktopIcons = JSON.parse(localStorage.getItem('webos-desktop-icons') || '[]');
      const filteredIcons = desktopIcons.filter(icon => icon.id !== 'music-player');
      if (filteredIcons.length !== desktopIcons.length) {
        localStorage.setItem('webos-desktop-icons', JSON.stringify(filteredIcons));
        console.log('Music Player app removed from desktop icons.');
      }
    } catch (error) {
      console.error('Error removing Music Player app:', error);
    }
  }, []);

  return (
    <div 
      className={`App ${theme}`}
      style={{
        backgroundImage: `url(${currentWallpaper})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.8s ease'
      }}
    >
      <Desktop 
        theme={theme} 
        openApps={openApps} 
        closeApp={closeApp} 
        launchApp={launchApp} 
        addAppToDesktop={addAppToDesktop}
        isMobile={isMobile}
        currentWallpaper={currentWallpaper}
        changeWallpaper={changeWallpaper}
      />
      <Taskbar 
        theme={theme} 
        toggleTheme={toggleTheme} 
        openApps={openApps} 
        launchApp={launchApp}
        closeApp={closeApp}
        isMobile={isMobile}
      />
    </div>
  );
}

export default App;